import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PurchaseFacade } from '@ridango/components';
import { NotificationsTypeMask, Product } from '@ridango/core';
import { phoneValidator } from '@ridango/core/lib/validators/phone.validator';

@Component({
    selector: 'rwidget-notifications',
    templateUrl: './notifications.component.html'
})
export class NotificationsComponent implements OnInit {
    @Input() step = null;
    @Input() productMask: Product;

    showOptions = false;
    notificationsForm: FormGroup;
    notificationsOptions: Array<any> = [];

    constructor(
        private fb: FormBuilder,
        private purchaseFacade: PurchaseFacade
    ) {
        this.notificationsForm = this.fb.group({
            selectedOptions: this.fb.array([])
        });
    }

    ngOnInit() {
        this.determineNotificationsTypesByMask(this.productMask).then((masks) => {
            this.notificationsOptions = [...masks];
        });
    }

    toggleView() {
        this.showOptions = !this.showOptions;
        if (!this.showOptions) {
            this.resetForm();
        }
    }

    onCheckboxChange(e) {
        const checkArray: FormArray = this.notificationsForm.get('selectedOptions') as FormArray;
        if (e.checked) {
            checkArray.push(new FormControl(e.source.value));
        } else {
            const index = checkArray.controls.findIndex((item: FormControl) => item.value === e.source.value);
            checkArray.removeAt(index);
        }

        this.checkInputs();
    }

    checkInputs() {
        const form = this.notificationsForm.getRawValue();
        const emailTypes = [
            NotificationsTypeMask.BOUGHT_EMAIL,
            NotificationsTypeMask.ACTIVATED_EMAIL,
            NotificationsTypeMask.ENDING_EMAIL
        ];
        const phoneTypes = [
            NotificationsTypeMask.BOUGHT_PHONE,
            NotificationsTypeMask.ACTIVATED_PHONE,
            NotificationsTypeMask.ENDING_PHONE
        ];

        if (emailTypes.some((i) => form.selectedOptions.includes(i))) {
            this.notificationsForm.addControl('email', new FormControl('', [Validators.required, Validators.email]));
        } else {
            this.notificationsForm.removeControl('email');
        }

        if (phoneTypes.some((i) => form.selectedOptions.includes(i))) {
            this.notificationsForm.addControl(
                'mobile_phone',
                new FormControl('', [Validators.required, phoneValidator, Validators.minLength(7)])
            );
        } else {
            this.notificationsForm.removeControl('mobile_phone');
        }

        this.submitForm();
    }

    submitForm() {
        if (this.notificationsForm.valid) {
            const form = this.notificationsForm.getRawValue();

            const notificationPayload = {
                ...form,
                notification_mask: form.selectedOptions.reduce((partialSum, a) => partialSum + a, 0)
            };
            delete notificationPayload.selectedOptions;

            this.purchaseFacade.setProductNotificationFormValidity(true);
            this.purchaseFacade.setProductNotification(notificationPayload);
        } else {
            this.purchaseFacade.setProductNotificationFormValidity(false);
        }
    }

    resetForm() {
        this.purchaseFacade.resetProductNotification();
        (this.notificationsForm.controls['selectedOptions'] as FormArray).clear();
        this.notificationsForm.removeControl('email');
        this.notificationsForm.removeControl('mobile_phone');
    }

    async determineNotificationsTypesByMask(mask): Promise<Product[]> {
        const NotificationMasks = {
            BOUGHT_EMAIL: 0, // 1
            ACTIVATED_EMAIL: 1, // 2
            ENDING_EMAIL: 2, // 4
            BOUGHT_PHONE: 3, // 8
            ACTIVATED_PHONE: 4, // 16
            ENDING_PHONE: 5 // 32
        };

        return new Promise((resolve) => {
            const masks = [];

            if ((mask & (1 << (NotificationMasks.BOUGHT_EMAIL || NotificationMasks.BOUGHT_PHONE))) !== 0) {
                const notificationCategory = {
                    title: 'PRODUCT_NOTIFICATION_CATEGORY_WHEN_BOUGHT'
                };
                if ((mask & (1 << NotificationMasks.BOUGHT_EMAIL)) !== 0) {
                    notificationCategory['email'] = NotificationsTypeMask.BOUGHT_EMAIL;
                }
                if ((mask & (1 << NotificationMasks.BOUGHT_PHONE)) !== 0) {
                    notificationCategory['phone'] = NotificationsTypeMask.BOUGHT_PHONE;
                }
                masks.push(notificationCategory);
            }
            if ((mask & (1 << (NotificationMasks.ACTIVATED_EMAIL || NotificationMasks.ACTIVATED_PHONE))) !== 0) {
                const notificationCategory = {
                    title: 'PRODUCT_NOTIFICATION_CATEGORY_WHEN_ACTIVATED'
                };
                if ((mask & (1 << NotificationMasks.ACTIVATED_EMAIL)) !== 0) {
                    notificationCategory['email'] = NotificationsTypeMask.ACTIVATED_EMAIL;
                }
                if ((mask & (1 << NotificationMasks.ACTIVATED_PHONE)) !== 0) {
                    notificationCategory['phone'] = NotificationsTypeMask.ACTIVATED_PHONE;
                }
                masks.push(notificationCategory);
            }
            if ((mask & (1 << (NotificationMasks.ENDING_EMAIL || NotificationMasks.ENDING_PHONE))) !== 0) {
                const notificationCategory = {
                    title: 'PRODUCT_NOTIFICATION_CATEGORY_WHEN_ENDING'
                };
                if ((mask & (1 << NotificationMasks.ENDING_EMAIL)) !== 0) {
                    notificationCategory['email'] = NotificationsTypeMask.ENDING_EMAIL;
                }
                if ((mask & (1 << NotificationMasks.ENDING_PHONE)) !== 0) {
                    notificationCategory['phone'] = NotificationsTypeMask.ENDING_PHONE;
                }
                masks.push(notificationCategory);
            }

            resolve(masks);
        });
    }
}
